import { useEffect, useState } from 'react';

import { POP_MS, REVEAL_DELAY_MS } from '../../constants';
import { useIsPageVisible } from '../useIsPageVisible';
import { usePrefersReducedMotion } from '../usePrefersReducedMotion';

type Result = Readonly<{
	/**
	 * The count the copy and effects show; it only catches up once the supporter is back on the page.
	 */
	displayCount: number;
	/**
	 * The count the DS stepper shows; it lags `displayCount` while the pop drawn over it plays, so the node
	 * reads as filling in rather than already being filled.
	 */
	settledCount: number;
}>;

/**
 * Paces the counter. A new `count` is only revealed once the page is visible, after `REVEAL_DELAY_MS`, so a
 * share made in another app plays out when the supporter is back, with the page scrolled back to the counter
 * first; the settled count then follows once the pop has finished.
 */
export function useRevealedCount(count: number): Result {
	const isPageVisible = useIsPageVisible();
	const prefersReducedMotion = usePrefersReducedMotion();
	const [displayCount, setDisplayCount] = useState(count);
	const [settledCount, setSettledCount] = useState(Math.max(0, count - 1));

	useEffect(() => {
		if (!isPageVisible || displayCount === count) return undefined;
		// The supporter may have shared from further down the page: bring the counter back into view while the
		// reveal waits, so the new step pops in where they can see it.
		window.scrollTo({ top: 0, behavior: prefersReducedMotion ? 'auto' : 'smooth' });
		const timer = setTimeout(() => {
			setDisplayCount(count);
			// Steps before the new one count as done even if this reveal cut their pop short.
			setSettledCount((settled) => Math.max(settled, count - 1));
		}, REVEAL_DELAY_MS);
		return () => clearTimeout(timer);
	}, [isPageVisible, count, displayCount, prefersReducedMotion]);

	useEffect(() => {
		if (settledCount === displayCount) return undefined;
		const timer = setTimeout(() => setSettledCount(displayCount), POP_MS);
		return () => clearTimeout(timer);
	}, [displayCount, settledCount]);

	return { displayCount, settledCount };
}
